import { useEffect, useMemo, useState } from "react";
import {
  CartesianGrid,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { Calculator, Loader2, Plus, Search, X } from "lucide-react";
import api from "../../services/api";
import { useToast } from "../ui/Toast";
import { evaluatePosition, payoffCurve } from "../../utils/blackScholes";

const RISK_FREE = 0.043;
const DAY = 86400000;

const emptyLeg = () => ({
  id: crypto.randomUUID(),
  type: "CALL",
  side: "BUY",
  quantity: 1,
  strike: "",
  expiry: "",
  entryPremium: "",
  iv: "",
});

const fmt = (n, d = 2) => (n == null || isNaN(n) ? "—" : Number(n).toFixed(d));
const fmtMoney = (n) =>
  n == null || isNaN(n) ? "—" : `${n < 0 ? "-" : ""}$${Math.abs(n).toFixed(2)}`;

// Normalize a date (string or Date) to YYYY-MM-DD using local components,
// so a logged trade's expiry matches the chain's "YYYY-MM-DD" keys.
const ymd = (d) => {
  if (!d) return "";
  if (typeof d === "string") return d.slice(0, 10);
  const dt = new Date(d);
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}-${String(dt.getDate()).padStart(2, "0")}`;
};

const positionLabel = (entry) => {
  const t = entry.trade;
  const sym = t.underlyingSymbol || t.symbol;
  if (t.legs && t.legs.length > 1) {
    return `${sym} · ${t.legs.length}-leg ${t.strategy || "spread"} · ${t.status}`;
  }
  return `${sym} ${t.strikePrice ?? ""} ${t.optionType || ""} ${ymd(t.expirationDate).slice(5)} ×${t.quantity} · ${t.status}`;
};

export default function OptionAnalytics() {
  const toast = useToast();
  const [ticker, setTicker] = useState("");
  const [loading, setLoading] = useState(false);
  const [chain, setChain] = useState(null); // {symbol, underlyingPrice, contracts, realtime, last}
  const [legs, setLegs] = useState([emptyLeg()]);

  // simulator sliders
  const [spot, setSpot] = useState(null); // overridable underlying price
  const [daysFwd, setDaysFwd] = useState(0);
  const [ivShift, setIvShift] = useState(0);

  // logged/synced option positions that can be loaded into the simulator
  const [savedPositions, setSavedPositions] = useState([]);

  useEffect(() => {
    api
      .get("/journal")
      .then(({ data }) => {
        const opts = (data.data.entries || []).filter(
          (e) => e.trade && (e.trade.securityType === "OPTION" || e.trade.optionType)
        );
        setSavedPositions(opts);
      })
      .catch(() => {});
  }, []);

  // Fetch quote + chain for a symbol and return the normalized chain object.
  const fetchChain = async (sym) => {
    const [{ data: q }, { data: c }] = await Promise.all([
      api.get(`/market/quote`, { params: { symbol: sym } }),
      api.get(`/market/option-chain`, { params: { symbol: sym, strikeCount: 40 } }),
    ]);
    const underlyingPrice = c.data.underlyingPrice ?? q.data.last;
    const chainObj = {
      symbol: sym,
      underlyingPrice,
      last: q.data.last,
      realtime: q.data.realtime,
      contracts: c.data.contracts || [],
    };
    setChain(chainObj);
    setSpot(underlyingPrice);
    setDaysFwd(0);
    setIvShift(0);
    return chainObj;
  };

  const fetchTicker = async () => {
    const sym = ticker.trim().toUpperCase();
    if (!sym) return;
    setLoading(true);
    try {
      await fetchChain(sym);
    } catch (e) {
      toast.error(e.response?.data?.error || "Failed to fetch market data. Is Schwab connected?");
    } finally {
      setLoading(false);
    }
  };

  // Load a logged position's legs, fetch its chain, and seed live IV per leg.
  const loadFromTrade = async (entry) => {
    const t = entry.trade;
    const sym = (t.underlyingSymbol || t.symbol || "").toUpperCase().split("_")[0];
    const src = t.legs && t.legs.length ? t.legs : [t];
    let newLegs = src.map((l) => ({
      id: crypto.randomUUID(),
      type: l.optionType || "CALL",
      side: l.side || "BUY",
      quantity: Math.abs(Number(l.quantity)) || 1,
      strike: l.strikePrice ?? "",
      expiry: ymd(l.expirationDate),
      entryPremium: l.entryPrice ?? "",
      iv: "",
    }));
    setTicker(sym);
    setLegs(newLegs);
    setLoading(true);
    try {
      const chainObj = await fetchChain(sym);
      newLegs = newLegs.map((l) => {
        const m = chainObj.contracts.find(
          (c) => c.type === l.type && c.expiration === l.expiry && Number(c.strike) === Number(l.strike)
        );
        return m && m.iv != null ? { ...l, iv: Number(m.iv).toFixed(2) } : l;
      });
      setLegs(newLegs);
      if (newLegs.some((l) => !l.iv)) {
        toast.info("Loaded. Some legs need IV — the live chain didn't have that exact contract (check strike/expiry).");
      }
    } catch (e) {
      toast.error(e.response?.data?.error || "Loaded legs, but couldn't fetch the live chain for IV.");
    } finally {
      setLoading(false);
    }
  };

  // available expirations + strikes from the chain
  const expirations = useMemo(() => {
    if (!chain) return [];
    return [...new Set(chain.contracts.map((c) => c.expiration))].sort();
  }, [chain]);

  const matchContract = (leg) =>
    chain?.contracts.find(
      (c) =>
        c.type === leg.type &&
        c.expiration === leg.expiry &&
        Number(c.strike) === Number(leg.strike)
    );

  const updateLeg = (id, patch) => {
    setLegs((prev) =>
      prev.map((l) => {
        if (l.id !== id) return l;
        const next = { ...l, ...patch };
        // auto-seed IV + entry premium from the matched live contract
        const m = matchContract(next);
        if (m) {
          if (patch.strike != null || patch.expiry != null || patch.type != null) {
            if (m.iv != null) next.iv = Number(m.iv).toFixed(2);
            if (!next.entryPremium && m.mark != null) next.entryPremium = Number(m.mark).toFixed(2);
          }
        }
        return next;
      })
    );
  };

  const addLeg = () => setLegs((p) => [...p, emptyLeg()]);
  const removeLeg = (id) => setLegs((p) => (p.length > 1 ? p.filter((l) => l.id !== id) : p));

  const validLegs = legs
    .filter((l) => l.strike && l.expiry && l.entryPremium !== "" && l.iv !== "")
    .map((l) => ({
      type: l.type,
      side: l.side,
      quantity: Number(l.quantity) || 1,
      strike: Number(l.strike),
      expiry: l.expiry,
      entryPremium: Number(l.entryPremium),
      iv: Number(l.iv),
    }));

  const asOf = useMemo(() => new Date(Date.now() + daysFwd * DAY), [daysFwd]);

  const result = useMemo(() => {
    if (!spot || validLegs.length === 0) return null;
    return evaluatePosition(validLegs, { S: spot, asOf, ivShiftPts: ivShift, r: RISK_FREE });
  }, [spot, asOf, ivShift, JSON.stringify(validLegs)]);

  const curve = useMemo(() => {
    if (!spot || validLegs.length === 0) return [];
    const lo = spot * 0.7;
    const hi = spot * 1.3;
    return payoffCurve(validLegs, { asOf, ivShiftPts: ivShift, r: RISK_FREE, low: lo, high: hi, points: 60 });
  }, [spot, asOf, ivShift, JSON.stringify(validLegs)]);

  const maxDte = useMemo(() => {
    if (validLegs.length === 0) return 365;
    const days = validLegs.map((l) => Math.max(0, (new Date(l.expiry + "T16:00:00") - Date.now()) / DAY));
    return Math.ceil(Math.max(...days, 1));
  }, [validLegs]);

  return (
    <div className="max-w-6xl mx-auto p-4 md:p-8 space-y-6 animate-fade-in">
      <div>
        <h1 className="text-3xl font-bold text-white tracking-tight flex items-center gap-2">
          <Calculator className="text-indigo-400" /> Option Lab
        </h1>
        <p className="text-slate-400 mt-1">
          Pull a live chain from your broker, build a position, and model P&amp;L and Greeks with Black-Scholes.
        </p>
      </div>

      {/* Ticker fetch */}
      <div className="flex gap-2">
        <div className="relative flex-1 max-w-xs">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
          <input
            value={ticker}
            onChange={(e) => setTicker(e.target.value.toUpperCase())}
            onKeyDown={(e) => e.key === "Enter" && fetchTicker()}
            placeholder="Ticker (e.g. AAPL)"
            className="w-full bg-slate-900 border border-slate-700 rounded-lg py-2 pl-9 pr-3 text-white font-mono focus:border-indigo-500 outline-none"
          />
        </div>
        <button
          onClick={fetchTicker}
          disabled={loading}
          className="bg-indigo-600 hover:bg-indigo-500 disabled:bg-slate-700 text-white px-5 rounded-lg font-medium flex items-center gap-2"
        >
          {loading ? <Loader2 size={16} className="animate-spin" /> : <Search size={16} />} Fetch
        </button>

        {savedPositions.length > 0 && (
          <select
            value=""
            onChange={(e) => {
              if (e.target.value !== "") loadFromTrade(savedPositions[Number(e.target.value)]);
            }}
            className="bg-slate-900 border border-slate-700 rounded-lg px-3 text-sm text-slate-300 max-w-[16rem] focus:border-indigo-500 outline-none cursor-pointer"
            title="Load a logged option position into the simulator"
          >
            <option value="">↓ Load from my trades…</option>
            {savedPositions.map((e, i) => (
              <option key={e.id} value={i}>{positionLabel(e)}</option>
            ))}
          </select>
        )}
      </div>

      {chain && (
        <>
          <div className="flex items-center gap-3 text-sm">
            <span className="text-white font-semibold text-lg">{chain.symbol}</span>
            <span className="font-mono text-emerald-400">${fmt(chain.underlyingPrice)}</span>
            <span
              className={`text-xs px-1.5 py-0.5 rounded ${
                chain.realtime ? "bg-emerald-500/10 text-emerald-400" : "bg-amber-500/10 text-amber-400"
              }`}
            >
              {chain.realtime ? "real-time" : "delayed"}
            </span>
          </div>

          {/* Legs editor */}
          <div className="bg-slate-800 rounded-xl border border-slate-700 p-4 space-y-3">
            {legs.map((leg, i) => {
              const m = matchContract(leg);
              return (
                <div key={leg.id} className="grid grid-cols-2 md:grid-cols-7 gap-2 items-end">
                  <Field label="Side">
                    <Toggle
                      value={leg.side}
                      options={["BUY", "SELL"]}
                      onChange={(v) => updateLeg(leg.id, { side: v })}
                      colors={{ BUY: "bg-emerald-600", SELL: "bg-red-600" }}
                    />
                  </Field>
                  <Field label="Type">
                    <Toggle
                      value={leg.type}
                      options={["CALL", "PUT"]}
                      onChange={(v) => updateLeg(leg.id, { type: v })}
                      colors={{ CALL: "bg-blue-600", PUT: "bg-orange-600" }}
                    />
                  </Field>
                  <Field label="Qty">
                    <input
                      type="number"
                      value={leg.quantity}
                      onChange={(e) => updateLeg(leg.id, { quantity: e.target.value })}
                      className={inputCls}
                    />
                  </Field>
                  <Field label="Expiry">
                    <select
                      value={leg.expiry}
                      onChange={(e) => updateLeg(leg.id, { expiry: e.target.value })}
                      className={inputCls}
                    >
                      <option value="">—</option>
                      {expirations.map((e) => (
                        <option key={e} value={e}>{e}</option>
                      ))}
                    </select>
                  </Field>
                  <Field label="Strike">
                    <input
                      type="number"
                      step="0.5"
                      value={leg.strike}
                      onChange={(e) => updateLeg(leg.id, { strike: e.target.value })}
                      className={inputCls}
                      placeholder={chain.underlyingPrice ? fmt(chain.underlyingPrice, 0) : ""}
                    />
                  </Field>
                  <Field label="Entry $">
                    <input
                      type="number"
                      step="0.01"
                      value={leg.entryPremium}
                      onChange={(e) => updateLeg(leg.id, { entryPremium: e.target.value })}
                      className={inputCls}
                      placeholder={m ? fmt(m.mark) : ""}
                    />
                  </Field>
                  <Field label="IV %">
                    <div className="flex gap-1 items-center">
                      <input
                        type="number"
                        step="0.1"
                        value={leg.iv}
                        onChange={(e) => updateLeg(leg.id, { iv: e.target.value })}
                        className={inputCls}
                        placeholder={m ? fmt(m.iv) : ""}
                      />
                      {legs.length > 1 && (
                        <button onClick={() => removeLeg(leg.id)} className="text-slate-500 hover:text-red-400">
                          <X size={16} />
                        </button>
                      )}
                    </div>
                  </Field>
                </div>
              );
            })}
            <button onClick={addLeg} className="flex items-center gap-1 text-sm text-indigo-400 hover:text-indigo-300">
              <Plus size={16} /> Add leg
            </button>
          </div>

          {result && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              {/* Sliders */}
              <div className="bg-slate-800 rounded-xl border border-slate-700 p-4 space-y-4">
                <Slider
                  label="Underlying"
                  value={spot}
                  min={chain.underlyingPrice * 0.5}
                  max={chain.underlyingPrice * 1.5}
                  step={0.5}
                  onChange={setSpot}
                  display={`$${fmt(spot)}`}
                  sub={`${spot >= chain.underlyingPrice ? "+" : ""}${fmt(((spot - chain.underlyingPrice) / chain.underlyingPrice) * 100, 1)}%`}
                />
                <Slider
                  label="Days forward"
                  value={daysFwd}
                  min={0}
                  max={maxDte}
                  step={1}
                  onChange={setDaysFwd}
                  display={`+${daysFwd}d`}
                  sub={asOf.toLocaleDateString()}
                />
                <Slider
                  label="IV shift"
                  value={ivShift}
                  min={-30}
                  max={30}
                  step={0.5}
                  onChange={setIvShift}
                  display={`${ivShift >= 0 ? "+" : ""}${fmt(ivShift, 1)} pts`}
                />
                <button
                  onClick={() => { setSpot(chain.underlyingPrice); setDaysFwd(0); setIvShift(0); }}
                  className="text-xs text-slate-400 hover:text-white underline"
                >
                  Reset to now
                </button>
              </div>

              {/* Net result */}
              <div className="bg-slate-800 rounded-xl border border-slate-700 p-4 lg:col-span-2">
                <div className="flex items-baseline justify-between mb-3">
                  <span className="text-slate-400 text-sm">Projected P&amp;L</span>
                  <span className={`text-3xl font-bold font-mono ${result.pnl >= 0 ? "text-emerald-400" : "text-red-400"}`}>
                    {result.pnl >= 0 ? "+" : ""}{fmtMoney(result.pnl)}
                  </span>
                </div>
                <div className="grid grid-cols-3 md:grid-cols-6 gap-2 text-center">
                  <Greek label="Value" value={fmtMoney(result.net.value)} />
                  <Greek label="Delta" value={fmt(result.net.delta)} />
                  <Greek label="Gamma" value={fmt(result.net.gamma, 3)} />
                  <Greek label="Theta/day" value={fmtMoney(result.net.theta)} />
                  <Greek label="Vega/1%" value={fmtMoney(result.net.vega)} />
                  <Greek label="Rho/1%" value={fmtMoney(result.net.rho)} />
                </div>
              </div>
            </div>
          )}

          {/* Payoff chart */}
          {curve.length > 0 && (
            <div className="bg-slate-800 rounded-xl border border-slate-700 p-4">
              <h3 className="text-sm font-semibold text-slate-300 mb-2">
                P&amp;L vs underlying — at {asOf.toLocaleDateString()} (IV {ivShift >= 0 ? "+" : ""}{fmt(ivShift, 1)})
              </h3>
              <ResponsiveContainer width="100%" height={260}>
                <LineChart data={curve} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                  <XAxis dataKey="S" tickFormatter={(v) => `$${v.toFixed(0)}`} stroke="#64748b" fontSize={11} />
                  <YAxis tickFormatter={(v) => `$${v.toFixed(0)}`} stroke="#64748b" fontSize={11} />
                  <Tooltip
                    contentStyle={{ background: "#0f172a", border: "1px solid #334155", borderRadius: 8 }}
                    formatter={(v) => [`$${Number(v).toFixed(2)}`, "P&L"]}
                    labelFormatter={(v) => `Underlying $${Number(v).toFixed(2)}`}
                  />
                  <ReferenceLine y={0} stroke="#64748b" />
                  <ReferenceLine x={spot} stroke="#6366f1" strokeDasharray="4 4" label={{ value: "now", fill: "#818cf8", fontSize: 10 }} />
                  <Line type="monotone" dataKey="pnl" stroke="#34d399" dot={false} strokeWidth={2} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Per-leg breakdown */}
          {result && (
            <div className="bg-slate-800 rounded-xl border border-slate-700 p-4 overflow-x-auto">
              <table className="w-full text-sm text-slate-300">
                <thead className="text-xs text-slate-500 uppercase">
                  <tr>
                    {["Leg", "Value", "Δ", "Θ/day", "Vega", "P&L"].map((h) => (
                      <th key={h} className="text-left py-1 pr-4 font-medium">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="font-mono">
                  {result.perLeg.map((l, i) => (
                    <tr key={i} className="border-t border-slate-700/50">
                      <td className="py-1.5 pr-4">
                        <span className={l.side === "SELL" ? "text-red-400" : "text-emerald-400"}>{l.side}</span>{" "}
                        {l.quantity} {l.type} {l.strike} {l.expiry?.slice(5)}
                      </td>
                      <td className="pr-4">{fmtMoney(l.price)}</td>
                      <td className="pr-4">{fmt(l.delta)}</td>
                      <td className="pr-4">{fmt(l.theta, 3)}</td>
                      <td className="pr-4">{fmt(l.vega, 3)}</td>
                      <td className={`pr-4 ${l.pnl >= 0 ? "text-emerald-400" : "text-red-400"}`}>
                        {l.pnl >= 0 ? "+" : ""}{fmtMoney(l.pnl)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}

const inputCls =
  "w-full bg-slate-900 border border-slate-700 rounded p-2 text-white font-mono text-sm focus:border-indigo-500 outline-none";

const Field = ({ label, children }) => (
  <div>
    <label className="text-[10px] text-slate-500 uppercase block mb-1">{label}</label>
    {children}
  </div>
);

const Toggle = ({ value, options, onChange, colors }) => (
  <div className="flex bg-slate-900 rounded border border-slate-700 p-0.5">
    {options.map((o) => (
      <button
        key={o}
        type="button"
        onClick={() => onChange(o)}
        className={`flex-1 text-[11px] font-bold py-1.5 rounded ${
          value === o ? `${colors[o]} text-white` : "text-slate-400 hover:text-white"
        }`}
      >
        {o}
      </button>
    ))}
  </div>
);

const Slider = ({ label, value, min, max, step, onChange, display, sub }) => (
  <div>
    <div className="flex justify-between items-baseline mb-1">
      <span className="text-xs text-slate-400">{label}</span>
      <span className="text-sm font-mono text-white">
        {display} {sub && <span className="text-slate-500 text-xs">{sub}</span>}
      </span>
    </div>
    <input
      type="range"
      min={min}
      max={max}
      step={step}
      value={value}
      onChange={(e) => onChange(parseFloat(e.target.value))}
      className="w-full h-1.5 bg-slate-700 rounded-lg appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-indigo-500"
    />
  </div>
);

const Greek = ({ label, value }) => (
  <div className="bg-slate-900/50 rounded-lg py-2">
    <div className="text-[10px] text-slate-500 uppercase">{label}</div>
    <div className="text-sm font-mono text-white">{value}</div>
  </div>
);
